import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/auth/useAuth'
import { useUser } from '@/hooks/users/useUser'
import { useSentry } from '@/hooks/utils/useSentry'
import { resetUserScopedQueries } from '@/utils/query/resetUserScopedQueries'

/**
 * Wall-clock cap on the gate. Long enough that a slow-but-alive logout still
 * completes before the form is shown, short enough that a hung request does not
 * strand the visitor behind the overlay.
 */
const FORCE_LOGOUT_TIMEOUT_MS = 8000

interface UseForceLogoutParams {
  /**
   * Comes from the scene's own config (`script.options.forceLogout`). When
   * false the hook is inert: it never fires a mutation and reports settled on
   * the first render, so every non-flagged auth screen keeps today's behavior.
   */
  enabled: boolean
}

interface UseForceLogoutResult {
  /**
   * UI gate: false while an existing session still has to be torn down. Flips
   * on the wall-clock cap too, so a hung logout never strands the visitor
   * behind a loading overlay — use it for what the visitor *sees*, never to
   * decide whether the session is gone.
   */
  isSettled: boolean
  /**
   * Redirect gate: false until the arriving session is known to be resolved.
   * Unlike `isSettled` the cap alone does not open it — a redirect fired on a
   * still-cached `isLoggedIn: true` bounces the visitor off the partner scene,
   * which is the exact failure the flag exists to prevent. On the cap it opens
   * only once a fresh profile read has come back, so the decision is made on
   * the server's answer rather than a stale cache.
   */
  isRedirectAllowed: boolean
}

/**
 * Drops any existing session when a partner scene asks for a clean slate
 * (e.g. `/entel`, where the visitor arrives from the partner to link a fresh
 * account and a leftover Fanatiz session would silently hijack the flow).
 *
 * Only the session the visitor *arrived with* is torn down. The decision is
 * frozen the first time the profile resolves, so a visitor who signs in on this
 * very screen is never logged back out — without that snapshot, `isLoggedIn`
 * flipping true after a successful login looks identical to arriving with a
 * stale session.
 */
export const useForceLogout = ({ enabled }: UseForceLogoutParams): UseForceLogoutResult => {
  const { isLoggedIn, profile, error: profileError, refetch: refetchProfile } = useUser()
  const { logoutMutation } = useAuth()
  const queryClient = useQueryClient()
  const { captureError } = useSentry()
  const hasTriggered = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // `null` until the profile first resolves; then true/false for "arrived with
  // a session". Adjusted during render rather than in an effect: an effect
  // would be a synchronous set-in-effect (lint error), and a ref cannot be read
  // during render to derive `isSettled`.
  //
  // Latched on the read having *resolved*, not on `!isLoading`: that flag is
  // `pending && fetching`, so it is already false for a profile read that
  // errored. Latching on it froze the decision at "anonymous" whenever a
  // transient 5xx hit `/profile`, skipping the teardown and leaving the visitor
  // on the partner scene with the exact session the flag exists to destroy.
  //
  // An errored read counts as "assume a session". Anonymous visitors get a
  // profile back (the API falls back to an anonymous session), so an error here
  // means we genuinely do not know — and the two mistakes are not symmetric:
  // logging out a visitor who has no session is a wasted request, while the
  // opposite leaks a live Fanatiz session into the partner linking flow.
  const [arrivedLoggedIn, setArrivedLoggedIn] = useState<boolean | null>(null)
  const hasProfileError = !!profileError
  const hasResolvedProfile = profile !== undefined || hasProfileError
  if (arrivedLoggedIn === null && hasResolvedProfile) {
    setArrivedLoggedIn(hasProfileError || isLoggedIn)
  }

  const needsLogout = enabled && arrivedLoggedIn === true
  // `logout()` is a bare fetch with no AbortSignal, so a stalled request never
  // rejects and the mutation never settles. Without this cap the overlay would
  // sit on the partner scene forever; setting it from a timer (not synchronously
  // in the effect) keeps it clear of the set-state-in-effect rule.
  const [hasTimedOut, setHasTimedOut] = useState(false)
  // Set once the cap's profile re-read comes back. Separate from `hasTimedOut`
  // so the overlay can drop immediately while the redirect keeps waiting for an
  // answer it can trust.
  const [hasVerifiedSession, setHasVerifiedSession] = useState(false)
  // Owned by the hook rather than read off the mutation, because
  // `isSuccess || isError` is also true in the window between a failed first
  // attempt and the retry started in the `catch`: the rejection and the retry's
  // `pending` are two separate state updates, so React can commit the `error`
  // one on its own. That commit would open both gates while the session is
  // still live and let AuthActor redirect off the partner scene mid-retry —
  // the exact failure `isRedirectAllowed` exists to prevent.
  const [hasFinishedLogout, setHasFinishedLogout] = useState(false)

  // Unmount only. Tying this to the effect below would defeat the cap: that
  // effect re-runs whenever `logoutMutation`'s identity changes, and the
  // `hasTriggered` latch would stop the cleared timer from being re-armed.
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
  }, [])

  useEffect(() => {
    if (!needsLogout || hasTriggered.current) return

    hasTriggered.current = true

    timeoutRef.current = setTimeout(() => {
      setHasTimedOut(true)
      // The mutation is still in flight, so the cached profile cannot be
      // trusted: the logout may already have landed server-side with only the
      // response stalling. Ask the server before opening the redirect gate —
      // logged-out keeps the visitor on the scene, and a session that really
      // did survive falls back to the normal post-auth redirect.
      // A failed read is still an answer — swallow it (and keep `.finally` from
      // re-throwing into an unhandled rejection) rather than trap the visitor
      // behind a gate that can no longer open.
      refetchProfile()
        .catch(() => undefined)
        .finally(() => setHasVerifiedSession(true))
    }, FORCE_LOGOUT_TIMEOUT_MS)

    const runLogout = async () => {
      try {
        await logoutMutation.mutateAsync()
      } catch (error) {
        try {
          // One retry: a partner landing that keeps the previous session
          // defeats the whole point of the flag.
          await logoutMutation.mutateAsync()
        } catch (retryError) {
          captureError(retryError, { firstAttemptError: error }, 'warning')
          // Drop what we control so the visitor is never stuck behind the
          // loading overlay. The server session may survive this — the profile
          // refetch is then authoritative and the scene falls back to its
          // normal post-auth redirect.
          sessionStorage.clear()
          resetUserScopedQueries(queryClient)
        }
      } finally {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        // Reached only once the retry has settled too, so no intermediate
        // mutation state can open the gates early.
        setHasFinishedLogout(true)
      }
    }

    runLogout()
    // captureError is re-created every render (useSentry returns fresh closures)
    // and would re-run this effect on every render; the ref latch makes a re-run
    // harmless, but leaving it out keeps the effect firing only on real changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLogout, logoutMutation, queryClient])

  // Nothing to tear down: flag off, or the visitor arrived anonymous.
  // `arrivedLoggedIn` never flips back, so a later login on this screen cannot
  // re-close either gate.
  const hasNothingToDo = !enabled || arrivedLoggedIn === false

  const isSettled = hasNothingToDo || hasFinishedLogout || hasTimedOut
  const isRedirectAllowed = hasNothingToDo || hasFinishedLogout || hasVerifiedSession

  return { isSettled, isRedirectAllowed }
}
